/**
 * Chat Step Renderer - Displays AI conversation using the real ChatPanel
 *
 * Handles sequential message display with typing indicators, then delegates
 * rendering to the shared ChatPanel component via its demo props.
 */

import React, { useState, useEffect } from 'react';
import { ChatConfig, FlowContext } from '../core/types';
import { GuidedFlowController } from '../core/GuidedFlowController';
import confetti from 'canvas-confetti';
import { ChatPanel } from '@/components/ui/chat-panel';
import type { Message, MessageAction as ChatMessageAction } from '@/types/chat.types';

export interface ChatStepRendererProps {
  config: ChatConfig;
  context: FlowContext;
  controller: GuidedFlowController;
  onComplete: () => void;
  isCelebration?: boolean;
  autoAdvance?: boolean;
  autoAdvanceDelay?: number;
  autoProgressEnabled?: boolean;
}

export const ChatStepRenderer: React.FC<ChatStepRendererProps> = ({
  config,
  context: _context,
  controller,
  onComplete,
  isCelebration = false,
  autoAdvance = false,
  autoAdvanceDelay = 2000,
  autoProgressEnabled = false,
}) => {
  const [visibleMessageIds, setVisibleMessageIds] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [allMessagesShown, setAllMessagesShown] = useState(false);
  const providers = controller.getProviders();

  // Trigger celebration confetti
  useEffect(() => {
    if (isCelebration) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    }
  }, [isCelebration]);

  // Show messages sequentially with delays
  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = [];

    config.messages.forEach((msg, index) => {
      const delay = msg.delay || 0;

      // Show typing indicator before assistant messages
      if (index > 0 && msg.role === 'assistant') {
        const typingTimeout = setTimeout(() => {
          setIsTyping(true);
        }, Math.max(0, delay - (config.typingDuration || 1000)));
        timeouts.push(typingTimeout);
      }

      // Show the message
      const messageTimeout = setTimeout(() => {
        setIsTyping(false);
        setVisibleMessageIds((prev) => {
          const next = [...prev, msg.id];
          if (next.length === config.messages.length) {
            setAllMessagesShown(true);
          }
          return next;
        });

        // Push to shared conversation context
        providers.chat.displayMessage({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(),
        });
      }, delay);
      timeouts.push(messageTimeout);
    });

    return () => timeouts.forEach(clearTimeout);
  }, [config, providers.chat]);

  // Auto-advance after all messages shown (if enabled)
  useEffect(() => {
    if (autoAdvance && allMessagesShown && autoProgressEnabled) {
      const timer = setTimeout(onComplete, autoAdvanceDelay);
      return () => clearTimeout(timer);
    }
  }, [autoAdvance, allMessagesShown, autoAdvanceDelay, autoProgressEnabled, onComplete]);

  // Build ChatPanel-compatible messages from visible flow messages
  const lastFlowMsg = config.messages[config.messages.length - 1];
  const demoMessages: Message[] = config.messages
    .filter((msg) => visibleMessageIds.includes(msg.id))
    .map((msg) => {
      const isLast = msg.id === lastFlowMsg?.id;
      const chatActions: ChatMessageAction[] = [];

      // Convert flow actions to ChatPanel custom actions
      if (msg.actions) {
        msg.actions.forEach((action, idx) => {
          chatActions.push({
            id: action.id || `action-${msg.id}-${idx}`,
            type: 'custom',
            label: action.label,
            variant: 'default',
            onAction: async () => {
              action.onClick?.();
              if (isLast) onComplete();
            },
          });
        });
      }

      // Convert suggestions to custom actions
      if (msg.suggestions) {
        msg.suggestions.forEach((suggestion, idx) => {
          chatActions.push({
            id: `suggestion-${msg.id}-${idx}`,
            type: 'custom',
            label: suggestion,
            variant: 'outline',
            onAction: async () => onComplete(),
          });
        });
      }

      return {
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp || new Date(),
        ...(chatActions.length > 0 ? { actions: chatActions } : {}),
      };
    });

  return (
    <div className="h-full relative">
      <ChatPanel
        demoMessages={demoMessages}
        demoLoading={isTyping}
        demoAction={{
          label: allMessagesShown ? 'Continue →' : 'Skip →',
          onClick: onComplete,
        }}
      />
    </div>
  );
};
